import React, { useState, useEffect } from 'react';
import { Select } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import './DateRangeFilter.css';

const { Option } = Select;

// Simplified date range presets
export const DATE_RANGE_PRESETS = {
  TODAY: 'today',
  THIS_WEEK: 'this_week',
  THIS_MONTH: 'this_month',
  LAST_MONTH: 'last_month',
  THIS_YEAR: 'this_year',
  ALL_TIME: 'all_time'
};

// Helper function to create a fresh dayjs instance to prevent mutations
const createFreshDayjs = () => dayjs();

// Helper function to get date range based on preset
export const getDateRangeFromPreset = (preset) => {
  switch (preset) {
    case DATE_RANGE_PRESETS.TODAY:
      return {
        startDate: createFreshDayjs().startOf('day').toDate(),
        endDate: createFreshDayjs().endOf('day').toDate(),
        label: 'Today'
      };
    
    case DATE_RANGE_PRESETS.THIS_WEEK:
      return {
        startDate: createFreshDayjs().startOf('week').toDate(),
        endDate: createFreshDayjs().endOf('week').toDate(),
        label: 'This Week'
      };
    
    case DATE_RANGE_PRESETS.THIS_MONTH:
      return {
        startDate: createFreshDayjs().startOf('month').toDate(),
        endDate: createFreshDayjs().endOf('month').toDate(),
        label: `This Month`
      };
    
    case DATE_RANGE_PRESETS.LAST_MONTH:
      return {
        startDate: createFreshDayjs().subtract(1, 'month').startOf('month').toDate(),
        endDate: createFreshDayjs().subtract(1, 'month').endOf('month').toDate(),
        label: `Last Month`
      };
    
    case DATE_RANGE_PRESETS.THIS_YEAR:
      return {
        startDate: createFreshDayjs().startOf('year').toDate(),
        endDate: createFreshDayjs().endOf('year').toDate(),
        label: `This Year`
      };
    
    case DATE_RANGE_PRESETS.ALL_TIME:
    default:
      return {
        startDate: null,
        endDate: null,
        label: 'All Time'
      };
  }
};

const DateRangeFilter = ({ 
  onDateRangeChange, 
  defaultPreset = DATE_RANGE_PRESETS.THIS_MONTH,
  className = '',
  controlled = false // New prop to control initialization behavior
}) => {
  const [selectedPreset, setSelectedPreset] = useState(defaultPreset);

  // Only initialize if not controlled (for backward compatibility)
  useEffect(() => {
    if (!controlled) {
      const initialRange = getDateRangeFromPreset(defaultPreset);
      onDateRangeChange(initialRange);
    }
  }, [defaultPreset, controlled, onDateRangeChange]);

  const handlePresetChange = (preset) => {
    console.log('🔄 DateRangeFilter: Preset changed to:', preset);
    setSelectedPreset(preset);
    const range = getDateRangeFromPreset(preset);
    console.log('📅 DateRangeFilter: Generated range:', {
      preset,
      startDate: range.startDate,
      endDate: range.endDate,
      label: range.label,
      startDateString: range.startDate?.toISOString(),
      endDateString: range.endDate?.toISOString()
    });
    onDateRangeChange(range);
  };

  const presetOptions = [
    { value: DATE_RANGE_PRESETS.TODAY, label: 'Today' },
    { value: DATE_RANGE_PRESETS.THIS_WEEK, label: 'This Week' },
    { value: DATE_RANGE_PRESETS.THIS_MONTH, label: 'This Month' },
    { value: DATE_RANGE_PRESETS.LAST_MONTH, label: 'Last Month' },
    { value: DATE_RANGE_PRESETS.THIS_YEAR, label: 'This Year' },
    { value: DATE_RANGE_PRESETS.ALL_TIME, label: 'All Time' }
  ];

  return (
    <div className={`date-range-filter-simple ${className}`}>
      <CalendarOutlined className="date-range-icon" />
      <Select
        value={selectedPreset}
        onChange={handlePresetChange}
        className="date-range-select"
        style={{ minWidth: 120 }}
      >
        {presetOptions.map(({ value, label }) => (
          <Option key={value} value={value}>
            {label}
          </Option>
        ))}
      </Select>
    </div>
  );
};

export default DateRangeFilter;
